// @ts-nocheck

import React, { FC, useEffect, useState, useRef, useContext } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { QRCodeGenerator } from 'src/shared/components';
import {
  ActionsSection,
  BrowserErrorModal,
  ExampleImagesSection,
  FileDownloadSection,
  FormValues,
  GameExpiredOverlay,
  GameHeader,
  ImagesSection,
  MusicSection,
  ResultsSection,
  TextFieldConfig,
  TextFieldsSection,
} from 'src/shared/components/Constructor';
import ImageFieldConfig from 'src/shared/components/Constructor/types';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { RealProgress } from '@shared/components/Constructor/GameCreationProgress';
import { MobileCropper } from '@shared/components/ImageCropper';
import type { GameKey } from '@shared/types';
import { getBrowserDiagnostics } from '@shared/utils/browser-detection';
import { isGameExpired, getEndEditDate } from '@shared/utils/gameExpiration';
import { audioBufferToWav } from '@shared/utils/audio-utils';

import { authContext } from '@store/index';

import * as ClientActions from './client.actions';
import { getIsClientFormDisabled } from './client.utils';

import styles from './client.module.scss';

interface ClientPageProps {
  gameKey: GameKey;
}

// Конфигурация полей для Client
const textFields: TextFieldConfig[] = [
  { name: 'startGameHeader', label: 'client.textBegin', placeholder: 'client.textBeginPlaceholder', tooltip: 'client.textBeginTooltip', required: false },
  { name: 'endGameHeader', label: 'client.textFinalMemo', placeholder: 'client.textFinalMemoPlaceholder', tooltip: 'client.textFinalMemoTooltip', required: false },
  { name: 'urlButton', label: 'client.linkUrl', placeholder: 'client.linkUrlPlaceholder', tooltip: 'client.linkTooltip', required: false },
];

const imageFields: ImageFieldConfig[] = [
  { name: 'startGameImage', label: 'client.selectMainImageMem', aspectRatio: 1, tooltip: 'client.selectMainImageTooltipMem', required: true, gridProps: { xs: 12, sm: 6, lg: 3 } },
  { name: 'endGameImage', label: 'client.selectFinalImageMem', aspectRatio: 1, tooltip: 'client.selectFinalImageTooltipMem', required: true, gridProps: { xs: 12, sm: 6, lg: 3 } },
];

// Конфигурация для 12 игровых плиток
const gameTilesFields: ImageFieldConfig[] = Array.from({ length: 12 }, (_, index) => ({
  name: `gameTiles.${index}`,
  label: `${index + 1}`,
  aspectRatio: 19 / 25,
  tooltip: `client.gameTile${index + 1}Tooltip`,
  required: true,
  gridProps: { xs: 12, sm: 6 },
}));

const ClientPage: FC<ClientPageProps> = ({ gameKey: initialGameKey }) => {
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
  }, []);

  const { t } = useTranslation();
  const [authData] = useContext(authContext);

  const [gameKey, setGameKey] = useState(initialGameKey);
  const { gameBuild: alreadyExistedGameBuild } = gameKey;
  const [_, setActivateGameDate] = ClientActions.useSetActivateGameDate();
  const gameDefaults = ClientActions.useFetchGameDefaults(gameKey.gameName);
  


  const [existingGameBuild, fetchGameBuild, gameBuildError] = ClientActions.useFetchGameBuild();

  const [createdGameBuild, handleGameBuildSubmit, gameBuildCreationLoading] =
    ClientActions.useCreateGameBuild();
  const [trimmedAudioFile, setTrimmedAudioFile] = useState<File | null>(null);
  const croppedPixelsDataRef = useRef<{ [key: string]: any }>({});
  const [showProgress, setShowProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [stepProgress, setStepProgress] = useState(0);
  const [showBrowserError, setShowBrowserError] = useState(false);

  // Убрали автоскролл к ошибке по запросу — оставляем только визуальное сообщение и цвет кнопки

  // ВАЖНО: объявляем gameBuild до всех useEffect!
  const gameBuild = createdGameBuild ?? existingGameBuild;
  const showGameLink = Boolean(gameBuild && gameBuild._id);
  
  // Отладочная информация
  // console.log('Current gameBuild:', gameBuild);
  // console.log('Current createdGameBuild:', createdGameBuild);
  // console.log('Current alreadyExistedGameBuild:', alreadyExistedGameBuild);

  // Проверка истекшей игры
  const [isGameExpiredState, setIsGameExpiredState] = useState(false);

  useEffect(() => {
    // Проверяем, истекла ли игра
    const expired = isGameExpired(gameKey.activateDate, gameKey.gameDayCount);
    setIsGameExpiredState(expired);
  }, [gameKey.activateDate, gameKey.gameDayCount]);

  // Загружаем данные игры при изменении gameBuildId
  useEffect(() => {
    if (gameKey.gameBuildId) {
      fetchGameBuild(gameKey.gameBuildId);
    } else {
    }
  }, [gameKey.gameBuildId, fetchGameBuild]);

  // Очищаем данные обрезки при изменении gameBuild
  useEffect(() => {
    if (gameBuild?._id) {
      croppedPixelsDataRef.current = {};
    }
  }, [gameBuild?._id]);

  // Определяем режим только для чтения
  const isReadOnly = authData?.isReadOnly || false;

  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
    reset,
    getValues,
    setValue,
    trigger,
  } = useForm<FormValues>({
    defaultValues: {
      ...textFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
      ...imageFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
      customMusicFile: '',
      urlButton: '',
      fileDownloadUrl: '',
      enableCustomMusic: false,
      language: 'English',
      gameTiles: Array(12).fill(''),
      withTime: true,
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (gameBuild) {
      const newValues: FormValues = {};
      
      // Заполняем текстовые поля
      textFields.forEach(field => {
        const keyMap: Record<string, keyof typeof gameBuild> = {
          startGameHeader: 'startGameHeader',
          endGameHeader: 'endGameHeader',
          urlButton: 'urlButton',
          fileDownload: 'urlButton',
        };
        const buildKey = keyMap[field.name];
        if (buildKey) {
          const value = gameBuild[buildKey] || '';
          newValues[field.name] = value;
        } else {
        }
      });
      
      // Заполняем поля изображений
      imageFields.forEach(field => {
        const keyMap: Record<string, keyof typeof gameBuild> = {
          startGameImage: 'startGameImageUrl',
          endGameImage: 'endGameImageUrl',
        };
        const buildKey = keyMap[field.name];
        if (buildKey) {
          const value = gameBuild[buildKey] || '';
          newValues[field.name] = value;
        } else {
        }
      });
      
      // Заполняем игровые плитки
      newValues.gameTiles = gameBuild.gameTilesUrls || Array(12).fill('');
      
      // Дополнительные поля
      newValues.customMusicFile = gameBuild.musicUrl || '';
      newValues.enableCustomMusic = !!gameBuild.musicUrl;
      // Разделяем: urlButton для URL, fileDownloadUrl для файла
      // Используем пустые строки вместо null для избежания предупреждений React
      newValues.urlButton = gameBuild.urlButton || '';
      newValues.fileDownloadUrl = gameBuild.fileDownloadUrl || '';
      newValues.language = gameBuild?.language || 'English';
      newValues.withTime = gameBuild?.timeAttemptsEnabled ?? true;
      
      console.log('ClientMemory: newValues prepared from gameBuild', newValues);
      reset(newValues);
      setTimeout(() => {}, 0);
    } else if (gameDefaults) {
      reset({
        customMusicFile: gameDefaults.defaultMusicUrl || '',
        enableCustomMusic: false,
        language: 'English',
        gameTiles: Array(12).fill(''),
        withTime: true,
      });
      setTimeout(() => {}, 0);
    } else {
      reset({
        customMusicFile: '',
        enableCustomMusic: false,
        language: 'English',
        gameTiles: Array(12).fill(''),
        withTime: true,
      });
      setTimeout(() => {}, 0);
    }
  }, [gameBuild?._id, reset, gameDefaults?._id, createdGameBuild?._id, existingGameBuild?._id]);

  // Обновляем gameKey после успешного создания игры
  useEffect(() => {
    if (createdGameBuild) {
      const result = createdGameBuild;
      if (result && result._id) {
        const newGameKey = {
          ...gameKey,
          gameBuild: result,
          gameBuildId: result._id,
        };
        setGameKey(newGameKey);
        
        setTimeout(() => {
          fetchGameBuild(result._id);
        }, 100);
        
        // Новый reset после полного обновления state
        const newValues: FormValues = {};
        textFields.forEach(field => {
          const keyMap: Record<string, keyof typeof createdGameBuild> = {
            startGameHeader: 'startGameHeader',
            endGameHeader: 'endGameHeader',
            urlButton: 'urlButton',
          };
          const buildKey = keyMap[field.name];
          if (buildKey) {
            newValues[field.name] = createdGameBuild[buildKey] || '';
          }
        });
        
        imageFields.forEach(field => {
          const keyMap: Record<string, keyof typeof createdGameBuild> = {
            startGameImage: 'startGameImageUrl',
            endGameImage: 'endGameImageUrl',
          };
          const buildKey = keyMap[field.name];
          if (buildKey) {
            newValues[field.name] = createdGameBuild[buildKey] || '';
          }
        });
        
        newValues.gameTiles = createdGameBuild.gameTilesUrls || Array(12).fill('');
        newValues.customMusicFile = createdGameBuild.musicUrl || '';
        newValues.enableCustomMusic = !!createdGameBuild.musicUrl;
        // Разделяем: urlButton для URL, fileDownloadUrl для файла
        newValues.urlButton = createdGameBuild.urlButton || '';
        newValues.fileDownloadUrl = createdGameBuild.fileDownloadUrl || '';
        newValues.language = createdGameBuild?.language || 'English';
        newValues.withTime = createdGameBuild?.timeAttemptsEnabled ?? true;
        
        reset(newValues);
        croppedPixelsDataRef.current = {};
        
        // Flush UI для timely обновления (картинки/текст)
        setTimeout(() => {}, 0);
      }
      if (!gameKey.activateDate) {
        setActivateGameDate(gameKey._id);
        setGameKey((prev) => ({
          ...prev,
          activateDate: new Date(),
        }));
      }
    }
  }, [createdGameBuild, fetchGameBuild, setActivateGameDate, reset]);

  const isCustomMusicEnabled = getValues('enableCustomMusic');
  const language = getValues('language');
    const [spinner, setSpinner] = useState(false);

  const onSubmit = async (data: FormValues) => {
    try {
      // Сначала выполняем валидацию, без показа прогресса
      
      // Жесткая проверка: все 12 игровых картинок должны быть заполнены
      const tilesForValidation = data.gameTiles || Array(12).fill('');
      const missingTileIndexes: number[] = [];
      for (let i = 0; i < 12; i++) {
        const val = tilesForValidation[i];
        if (!val || (typeof val === 'string' && val.trim() === '')) {
          missingTileIndexes.push(i + 1);
        }
      }
      if (missingTileIndexes.length > 0) {
        const fieldsList = missingTileIndexes.map((i) => `📝 ${t('clientMemory.imageSectionTitle')} #${i}`).join('\n');
        const errorMessage = `${t('clientMemory.validation.requiredFieldsMessage')}\n\n${fieldsList}`;
        setError('root', { type: 'manual', message: errorMessage });
        setError('root.server', { type: 'manual', message: errorMessage } as any);
        setShowProgress(false);
        setCurrentStep('');
        setStepProgress(0);
        return; // Блокируем создание
      }

      // Валидация пройдена — показываем прогресс
      setShowProgress(true);
      setCurrentStep('validation');
      setStepProgress(100);
      
      // Проверяем, есть ли данные обрезки для игровых плиток
      const tileCropData = {};
      for (let i = 0; i < 12; i++) {
        const fieldName = `gameTiles.${i}`;
        if (croppedPixelsDataRef.current[fieldName]) {
          tileCropData[fieldName] = croppedPixelsDataRef.current[fieldName];
        }
      }
      
      // Автоматическая обрезка и компрессия музыки, если она новая
      let audioFileToUse = trimmedAudioFile || data.customMusicFile;
      
      // Если есть музыкальный файл и он новый (не обрезанный), запускаем автоматическую обрезку
      if (data.customMusicFile instanceof File && !trimmedAudioFile) {
        setCurrentStep('music');
        setStepProgress(10);
        
        try {
          // Создаем AudioContext для обрезки
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          
          // Читаем файл
          const arrayBuffer = await data.customMusicFile.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          // Используем полную длительность аудио без автоматической обрезки
          const startTime = 0;
          const endTime = audioBuffer.duration;
          
          // Вычисляем сэмплы для обрезки
          const sampleRate = audioBuffer.sampleRate;
          const startSample = Math.floor(startTime * sampleRate);
          const endSample = Math.floor(endTime * sampleRate);
          const trimmedLength = endSample - startSample;
          
          // Создаем новый AudioBuffer для обрезанного аудио
          const trimmedBuffer = audioContext.createBuffer(
            audioBuffer.numberOfChannels,
            trimmedLength,
            sampleRate
          );
          
          // Копируем данные из выбранного диапазона
          for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
            const originalData = audioBuffer.getChannelData(channel);
            const trimmedData = trimmedBuffer.getChannelData(channel);
            
            for (let i = 0; i < trimmedLength; i++) {
              trimmedData[i] = originalData[startSample + i];
            }
          }
          
          // Конвертируем AudioBuffer в MP3 используя MP3Compressor
          const { MP3Compressor } = await import('@shared/utils/mp3-compression');
          const compressor = new MP3Compressor();
          
          // Сохраняем оригинальное название файла
          const originalFileName = data.customMusicFile.name;
          const fileNameWithoutExt = originalFileName.replace(/\.[^/.]+$/, '');
          
          // Создаем WAV файл из AudioBuffer
          const wavBlob = await audioBufferToWav(trimmedBuffer);
          const tempFile = new File([wavBlob], `${fileNameWithoutExt}.wav`, { type: 'audio/wav' });
          
          const compressionResult = await compressor.compressMP3(tempFile);
          const trimmedFile = compressionResult.compressedFile;
          
          audioFileToUse = trimmedFile;
          // Проверяем состояние AudioContext перед закрытием
          if (audioContext.state !== 'closed') {
            try {
              audioContext.close();
            } catch (error) {
              // console.error('Ошибка при закрытии AudioContext:', error);
            }
          }
          
        } catch (error) {
          console.error('Ошибка автоматической обрезки музыки:', error);
          // Если обрезка не удалась, используем оригинальный файл
          audioFileToUse = data.customMusicFile;
        }
      }
      
      // Создаем копию данных с обновленной музыкой
      const submitData = { 
        _id: gameKey.gameBuildId, 
        ...data, 
        customMusicFile: audioFileToUse,
        fileDownload: data.fileDownload // Добавляем файл для скачивания
      };
      
      // Убеждаемся, что gameTiles передаются как массив и сохраняем их в submitData
      const gameTiles = data.gameTiles || Array(12).fill('');
      submitData.gameTiles = gameTiles;
      
      // Дополнительная проверка: убеждаемся, что gameTiles не пустой массив
      if (!submitData.gameTiles || submitData.gameTiles.length === 0) {
        console.warn('gameTiles is empty, initializing with default values');
        submitData.gameTiles = Array(12).fill('');
      }
      
      setCurrentStep('images');
      setStepProgress(30);
      
      setCurrentStep('upload');
      setStepProgress(60);
      
      await handleGameBuildSubmit(
        submitData,
        croppedPixelsDataRef.current,
        gameTiles
      );
      
      setCurrentStep('creation');
      setStepProgress(80);

      // Имитируем завершение создания
      setTimeout(() => {
        setCurrentStep('complete');
        setStepProgress(100);
        // Скрываем сразу после complete
        setShowProgress(false);
        handleProgressComplete();
      }, 1000);
      
    } catch (error) {
      console.error('CMemory:onSubmit -> error:', error);
      setError('root', { type: 'manual', message: 'Ошибка при сохранении игры' });
      setError('root.server', { type: 'manual', message: 'Ошибка при сохранении игры' } as any);
      setCurrentStep('error');
      setShowProgress(false);
      setCurrentStep('');
      setStepProgress(0);
    } finally {
      // Гарантированное скрытие progress
      setTimeout(() => {
        setShowProgress(false);
        setCurrentStep('');
        setStepProgress(0);
        handleProgressComplete();
      }, 500);  // 0.5 сек delay для показа error, если есть
    }
  };

  const handleProgressComplete = () => {
    setShowProgress(false);
    setCurrentStep('');
    setStepProgress(0);
  };
  
  const onError = (errors: any) => {
    console.log('CMemory:onError -> RHF errors:', errors);
    const missingFields = [
      ...textFields.filter(f => f.required && errors[f.name]),
      ...imageFields.filter(f => f.required && errors[f.name]),
    ];

    // Добавляем недостающие карточки, если есть
    const tileErrors = Array.isArray(errors?.gameTiles) ? errors.gameTiles : [];
    const missingTileIndexes: number[] = [];
    tileErrors.forEach((err: any, idx: number) => {
      if (err) missingTileIndexes.push(idx + 1);
    });

    if (missingFields.length > 0 || missingTileIndexes.length > 0) {
      console.log('CMemory:onError -> missing required fields:', missingFields);
      const fieldsList = [
        ...missingFields.map(({ label }) => `📝 ${t(label)}`),
        ...(missingTileIndexes.length ? [
          `🧩 ${t('clientMemory.imageSectionTitle')}: ${missingTileIndexes.map((i) => `#${i}`).join(', ')}`
        ] : [])
      ].join('\n');
      const errorMessage = `${t('clientMemory.validation.requiredFieldsMessage')}\n\n${fieldsList}`;
      setError('root', { type: 'manual', message: errorMessage });
      setError('root.server', { type: 'manual', message: errorMessage } as any);
      // Без автоскролла — только визуальное сообщение
    }
  };

  const handleTestGameClick = () => {
    if (gameBuild && gameBuild._id) {
      window.open(`/game?buildId=${gameBuild._id}&isTesting=true`, '_blank');
    }
  };

  // Функция для множественной загрузки изображений
  const handleUploadMultipleImages = async (event) => {
    const files = event?.target?.files || [];

    if (!files || files.length === 0) {
      return;
    }

    // Берем только первые 14 файлов, остальные игнорируем
    const filesArray = Array.from(files).slice(0, 14);

    while (filesArray.length < 14) {
      filesArray.push(null);
    }

    // Для startGameImage и endGameImage, проверяем и обновляем значения, если изображение есть
    if (filesArray[0] instanceof File) {
      setValue('startGameImage', filesArray[0]);
    }
    if (filesArray[1] instanceof File) {
      setValue('endGameImage', filesArray[1]);
    }

    // Для gameTiles (с 2 до 13), проверяем и обновляем значения, если изображение есть
    filesArray.slice(2).forEach((file, index) => {
      const fieldName = `gameTiles.${index}`;
      if (file instanceof File) {
        setValue(fieldName, file);
      }
    });

    // Проверяем, были ли загружены изображения
    setTimeout(() => {
      const currentValues = getValues();
      const hasMainImage = currentValues.startGameImage instanceof File;
      const hasFinalImage = currentValues.endGameImage instanceof File;
      const hasGameTiles = currentValues.gameTiles?.some(tile => tile instanceof File);
      
      // Если изображения не загрузились, показываем ошибку браузера
      if (!hasMainImage && !hasFinalImage && !hasGameTiles) {
        const diagnostics = getBrowserDiagnostics();
        if (diagnostics.browser.isEmbedded || !diagnostics.isCompatible) {
          setShowBrowserError(true);
        }
      }
    }, 100);
  };

  if (!gameDefaults) {
    return null;
  }

  const { activateDate, editDayCount, gameDayCount } = gameKey;

  const isClientFormDisabled = getIsClientFormDisabled(
    activateDate,
    editDayCount
  ) || isReadOnly || isGameExpiredState;

  return (
    <Container maxWidth={false} className={styles.gameContainer} sx={{ maxWidth: '900px' }}>
      <GameHeader
        gameDefaults={gameDefaults}
        activateDate={activateDate ? (typeof activateDate === 'string' ? new Date(activateDate) : activateDate) : null}
        editDayCount={editDayCount}
        gameDayCount={gameDayCount}
      />

      <ExampleImagesSection gameDefaults={gameDefaults} />



        {/* Компонент прогресса создания игры */}
        {showProgress && (
          <RealProgress
            isVisible={showProgress}
            onComplete={handleProgressComplete}
            currentStep={currentStep}
            stepProgress={stepProgress}
          />
        )}
        
        <form onSubmit={handleSubmit(onSubmit, onError)}>
        {/* Секция множественной загрузки изображений */}
        <Box className={styles.memoryBlock}>
          <Typography variant="h6" className={styles.memoryBlockTitle}>
            {t('clientMemory.selectMultipleImages')}
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, color: 'text.secondary' }}>
            {t('clientMemory.selectMultipleImagesSubtitle')}
          </Typography>
          <Grid container justifyContent="center">
            <Button
              className={styles.uploadMultipleButton}
              variant="contained"
              component="label"
              disabled={isReadOnly}
            >
              {t('clientMemory.uploadMultiple')}
              <input
                type="file"
                multiple
                accept="image/*"
                hidden
                onChange={handleUploadMultipleImages}
                disabled={isReadOnly}
              />
            </Button>
          </Grid>
        </Box>

        {/* Блок 1: Начальный экран */}
        <Box className={styles.memoryBlock}>
          <Typography variant="h6" className={styles.memoryBlockTitle}>
            {t('clientMemory.textContent')}
          </Typography>
          
          {/* Аккордеон с примером изображения */}
          {gameDefaults?.startExampleImage && (
            <Box sx={{ mb: 2 }}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="example-image-content"
                  id="example-image-header"
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      justifyContent: 'center'
                    }
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      color: 'primary.main',
                      fontWeight: 500
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                    {t('clientMemory.exampleImage') || 'Посмотреть пример'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <img
                      src={Array.isArray(gameDefaults.startExampleImage) 
                        ? gameDefaults.startExampleImage[0] 
                        : gameDefaults.startExampleImage}
                      alt="Пример изображения для начального экрана"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}
                      onError={(e) => {
                        console.error('Failed to load example image:', e);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="startGameHeader"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                                        <TextField
                        {...field}
                        disabled={isClientFormDisabled}
                        error={!!errors?.startGameHeader}
                        fullWidth
                        label={t('clientMemory.textBegin')}
                        placeholder={t('clientMemory.textBeginPlaceholder')}
                        sx={{ 
                          '& .MuiInputBase-root': {
                            textAlign: 'center'
                          },
                          '& .MuiInputLabel-root': {
                            textAlign: 'center'
                          }
                        }}
                      />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="startGameImage"
                control={control}
                rules={{ required: t('clientMemory.selectMainImageReqMem') }}
                render={({ field }) => (
                  <MobileCropper
                    label={t('clientMemory.selectMainImageMem')}
                    value={field.value}
                    onChange={field.onChange}
                    aspectRatio={1}
                    isDisabled={isClientFormDisabled}
                    maskImage={Array.isArray(gameDefaults.maskImageUrl)
                      ? gameDefaults.maskImageUrl[0] || ''
                      : gameDefaults.maskImageUrl || ''}
                    // toolTipContent={t('clientMemory.selectMainImageTooltipMem')}
                    fieldName="startGameImage"
                    onCroppedPixelsChange={(fieldName, croppedPixels) => {
                      croppedPixelsDataRef.current[fieldName] = croppedPixels;
                    }}
                    hasError={!!errors.startGameImage}
                    errorMessage={errors.startGameImage?.message as string}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>



        {/* Блок 2: Финальный экран */}
        <Box className={styles.memoryBlock}>
          <Typography variant="h6" className={styles.memoryBlockTitle}>
            {t('clientMemory.finalContent')}
          </Typography>
          
          {/* Аккордеон с примером изображения для финального экрана */}
          {gameDefaults?.endExampleImage && (
            <Box sx={{ mb: 2 }}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="example-final-image-content"
                  id="example-final-image-header"
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      justifyContent: 'center'
                    }
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      color: 'primary.main',
                      fontWeight: 500
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                    {t('clientMemory.exampleFinalImage') || 'Посмотреть пример финального изображения'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <img
                      src={Array.isArray(gameDefaults.endExampleImage) 
                        ? gameDefaults.endExampleImage[0] 
                        : gameDefaults.endExampleImage}
                      alt="Пример изображения для финального экрана"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}
                      onError={(e) => {
                        console.error('Failed to load final example image:', e);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="endGameHeader"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                                        <TextField
                        {...field}
                        disabled={isClientFormDisabled}
                        error={!!errors?.endGameHeader}
                        fullWidth
                        label={t('clientMemory.textFinalMemo')}
                        placeholder={t('clientMemory.textFinalMemoPlaceholder')}
                        sx={{ 
                          '& .MuiInputBase-root': {
                            textAlign: 'center'
                          },
                          '& .MuiInputLabel-root': {
                            textAlign: 'center'
                          }
                        }}
                      />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="endGameImage"
                control={control}
                rules={{ required: t('clientMemory.selectFinalImageReqMem') }}
                render={({ field }) => (
                  <MobileCropper
                    label={t('clientMemory.selectFinalImageMem')}
                    value={field.value}
                    onChange={field.onChange}
                    aspectRatio={1}
                    isDisabled={isClientFormDisabled}
                    maskImage={Array.isArray(gameDefaults.maskImageUrl)
                      ? gameDefaults.maskImageUrl[1] ||
                        gameDefaults.maskImageUrl[0] ||
                        ''
                      : gameDefaults.maskImageUrl || ''}
                    // toolTipContent={t('clientMemory.selectFinalImageTooltipMem')}
                    fieldName="endGameImage"
                    onCroppedPixelsChange={(fieldName, croppedPixels) => {
                      croppedPixelsDataRef.current[fieldName] = croppedPixels;
                    }}
                    hasError={!!errors.endGameImage}
                    errorMessage={errors.endGameImage?.message as string}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        

        {/* Блок 4: Игровые карточки */}
        <Box className={styles.memoryBlock}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ImagesSection 
              control={control} 
              errors={errors} 
              isReadOnly={isReadOnly} 
              cropDataRef={croppedPixelsDataRef} 
              gameType="memory"
              gameDefaults={gameDefaults}
              images={gameTilesFields.map((field) => ({
                ...field,
                // maskImage: Array.isArray(gameDefaults.maskImageUrl)
                //   ? gameDefaults.maskImageUrl[2] ||
                //     gameDefaults.maskImageUrl[0] ||
                //     ''
                //   : gameDefaults.maskImageUrl || '',
              }))}
            />
          </Box>
        </Box>

        {/* Блок 5: Музыка */}
        <Box className={styles.memoryBlock}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <MusicSection
              control={control}
              errors={errors}
              isReadOnly={isReadOnly}
              setError={setError}
              onTrimmedFileChange={setTrimmedAudioFile}
              musicFieldName="customMusicFile"
              waveformTrimmerFieldName="customMusicFile"
            />
          </Box>
        </Box>

        {/* Блок 6: Загрузка файла */}
        <Box className={styles.memoryBlock}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <FileDownloadSection
              control={control}
              errors={errors}
              isReadOnly={isReadOnly}
              setError={setError}
              setValue={setValue}
              getValues={getValues}
              fieldName="urlButton"
            />
          </Box>
        </Box>

        <ActionsSection
          gameBuild={gameBuild}
          gameBuildCreationLoading={gameBuildCreationLoading}
          isReadOnly={isReadOnly}
          onTestGameClick={handleTestGameClick}
          hasErrors={!!errors.root || Object.keys(errors).length > 0}
        />

        <ResultsSection
          errors={errors}
          gameBuild={gameBuild}
          showGameLink={showGameLink}
        />

        {/* QR-код компонент */}
        <QRCodeGenerator
          gameUrl={gameBuild ? `${window.location.origin}/game?buildId=${gameBuild._id}` : ''}
          gameBuildId={gameBuild?._id}
          showGameLink={showGameLink}
        />
      </form>

      {/* Оверлей с уведомлением об истечении срока */}
      <GameExpiredOverlay isVisible={isGameExpiredState} />
      
      {/* Модальное окно с ошибкой браузера */}
      <BrowserErrorModal 
        isOpen={showBrowserError} 
        onClose={() => setShowBrowserError(false)}
        gameType="memory"
      />
    </Container>
  );
};

export default ClientPage;
